import React, { useCallback } from 'react';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { getIsoUtcDate } from 'helpers/dayJsFormats';
import { Tooltip, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import FormInputProps from './FormInputProps';

type Props<T extends FieldValues = any> = Omit<
  FormInputProps.TimePickerInput,
  'inputType'
> &
  UseControllerProps<T>;

const DatePickerInput: React.FC<Props> = (props: Props) => {
  const {
    control,
    name,
    rules,
    shouldUnregister,
    defaultValue,
    TextFieldProps,
    onChange,
    label,
    tooltip,
    ...rest
  } = props;
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
    rules,
    shouldUnregister,
    defaultValue,
  });

  const handleChange = useCallback(
    (value: Dayjs | null) => {
      try {
        field.onChange(value ? getIsoUtcDate(value) : null);
      } catch (e) {
        field.onChange(null);
      }
    },
    [field],
  );

  return (
    <TimePicker
      inputRef={field.ref}
      value={field.value ? dayjs.utc(field.value) : null}
      onChange={(value) =>
        onChange ? onChange(value, field.onChange) : handleChange(value)
      }
      closeOnSelect
      label={
        tooltip ? (
          <Typography display="flex" gap={1}>
            {label}
            <Tooltip title={tooltip}>
              <InfoOutlined fontSize="small" />
            </Tooltip>
          </Typography>
        ) : (
          label
        )
      }
      {...rest}
      slotProps={{
        field: {
          clearable: true,
          onClear: () =>
            onChange ? onChange(null, field.onChange) : handleChange(null),
        },
        textField: {
          ...TextFieldProps,
          variant: 'outlined',
          name: field.name,
          onBlur: field.onBlur,
          error: !!error,
          helperText: error && error.message,
          InputProps: {
            notched: false,
          },
        },
      }}
    />
  );
};

export default DatePickerInput;
